import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiX, FiSend, FiArrowLeft, FiCheck, FiArrowRight, FiMessageSquare } from 'react-icons/fi';
import { RiSparklingFill } from 'react-icons/ri';
import '../assets/styles/ChatBot.scss';

interface Message {
  id: number;
  from: 'bot' | 'user';
  text: string;
  seen?: boolean;
  typewriter?: boolean;
}

interface SectionLink {
  label: string;
  sectionId: string;
}

interface QuickQuestion {
  label: string;
  answer: string;
  sectionLink?: SectionLink;
}

const QUICK_QUESTIONS: QuickQuestion[] = [
  {
    label: '👨‍💻 What do you specialize in?',
    answer: `I'm Ameer Hamza, a Senior Software Engineer with 8+ years of experience. I specialize in:\n\n• Native Android & iOS development\n• React Native & KMP cross-platform apps\n• AI & LLM-powered mobile/web apps\n• Shopify embedded apps & backend systems\n\nBasically — if it involves mobile or AI, I've built it.`,
    sectionLink: { label: 'View My Skills', sectionId: 'skills' },
  },
  {
    label: '🤖 Can you build an AI-powered app?',
    answer: `Absolutely — that's one of my core strengths. I've built apps integrating:\n\n• OpenAI, Claude, and Gemini APIs\n• RAG pipelines & AI agents\n• On-device AI with Core ML & MediaPipe\n• LangChain & LlamaIndex workflows\n\nWhether it's a chatbot, smart recommendation engine, or computer vision feature — I can build it end to end.`,
    sectionLink: { label: 'See My Expertise', sectionId: 'expertise' },
  },
  {
    label: '📱 Do you build for both Android & iOS?',
    answer: `Yes — I cover the full mobile spectrum:\n\n• Native Android (Kotlin, Jetpack Compose)\n• Native iOS (Swift, SwiftUI)\n• Cross-platform (React Native, KMP)\n\nI recommend the right approach based on your project needs and budget. Quality is non-negotiable regardless of platform.`,
    sectionLink: { label: 'View My Projects', sectionId: 'projects' },
  },
  {
    label: '⏱️ What is your typical timeline?',
    answer: `It depends on scope, but here's a general guide:\n\n• Simple app (MVP): 2–4 weeks\n• Mid-complexity app: 4–8 weeks\n• Full product with backend + AI: 8–16 weeks\n\nI always provide a detailed estimate before starting. I'm known for delivering on time — check my client reviews!`,
    sectionLink: { label: 'Read Client Reviews', sectionId: 'reviews' },
  },
  {
    label: '💰 What are your rates?',
    answer: `My rates vary by project type and complexity. I work on both:\n\n• Fixed-price projects (clear scope)\n• Hourly engagements (ongoing work)\n\nI'm available on Fiverr and Upwork with verified reviews, or we can work directly. The best way to get an accurate quote is to share your project details — I'll respond within hours.`,
    sectionLink: { label: 'Get in Touch', sectionId: 'contact' },
  },
  {
    label: '⭐ How experienced are you?',
    answer: `8+ years of professional experience with:\n\n• 70+ happy clients on Fiverr & Upwork\n• 100+ projects delivered\n• 98% success rate\n• 5.0 average rating across platforms\n\nI've worked with clients from Switzerland, the US, UK, Canada, and 30+ other countries. You're in good hands.`,
    sectionLink: { label: 'See My Career History', sectionId: 'history' },
  },
  {
    label: '📞 How do I hire you?',
    answer: `Easy — a few options:\n\n• Fill out the contact form on this page\n• Reach me on Fiverr: fiverr.com/ameerhamza9990\n• Find me on Upwork: search "Ameer Hamza"\n• Connect on LinkedIn\n\nI typically respond within a few hours. Let's build something great together! 🚀`,
    sectionLink: { label: 'Contact Me Now', sectionId: 'contact' },
  },
];

const INITIAL_MESSAGE: Message = {
  id: 0,
  from: 'bot',
  text: "Hi! 👋 I'm Ameer's AI assistant. Ask me anything about his skills, experience, or how to hire him. Or pick a quick question below:",
};

// Typing delay based on 12ms per char, clamped between 800ms and 2s
function typingDelay(text: string): number {
  return Math.min(2000, Math.max(800, text.length * 12));
}

let msgId = 1;

// Individual bot message with typewriter
function BotMessage({ msg, onDone, onTick }: { msg: Message; onDone: (id: number) => void; onTick: () => void }) {
  const [displayed, setDisplayed] = useState(msg.typewriter ? '' : msg.text);

  useEffect(() => {
    if (!msg.typewriter) {
      setDisplayed(msg.text);
      return;
    }
    setDisplayed('');
    let i = 0;
    const interval = setInterval(() => {
      i++;
      setDisplayed(msg.text.slice(0, i));
      onTick();
      if (i >= msg.text.length) {
        clearInterval(interval);
        onDone(msg.id);
      }
    }, 12);
    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [msg.id]);

  return (
    <div className="chatbot-msg__bubble">
      {displayed.split('\n').map((line, i, arr) => (
        <span key={i}>{line}{i < arr.length - 1 && <br />}</span>
      ))}
    </div>
  );
}

function ChatBot() {
  const [isOpen, setIsOpen] = useState(false);
  const [view, setView] = useState<'questions' | 'chat'>('questions');
  const [messages, setMessages] = useState<Message[]>([INITIAL_MESSAGE]);
  const [isTyping, setIsTyping] = useState(false);
  const [hasUnread, setHasUnread] = useState(true);
  const [activeQuestion, setActiveQuestion] = useState<QuickQuestion | null>(null);
  const [showActions, setShowActions] = useState(false);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const chatWindowRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen) setHasUnread(false);
  }, [isOpen]);

  // Safari: block all scroll events on the chat window from reaching the page,
  // except when the event originates inside the scrollable messages container
  useEffect(() => {
    const win = chatWindowRef.current;
    if (!win) return;

    const onWheel = (e: WheelEvent) => {
      const messages = messagesContainerRef.current;
      const quickList = win.querySelector('.chatbot-window__quick-list') as HTMLElement | null;

      // Allow scroll only if inside the messages container or quick list and they can still scroll
      for (const el of [messages, quickList]) {
        if (el && el.contains(e.target as Node)) {
          const { scrollTop, scrollHeight, clientHeight } = el;
          const atTop = scrollTop === 0 && e.deltaY < 0;
          const atBottom = scrollTop + clientHeight >= scrollHeight && e.deltaY > 0;
          if (!atTop && !atBottom) return; // let it scroll normally inside
        }
      }
      e.preventDefault();
    };

    const onTouchMove = (e: TouchEvent) => {
      const messages = messagesContainerRef.current;
      const quickList = win.querySelector('.chatbot-window__quick-list') as HTMLElement | null;

      for (const el of [messages, quickList]) {
        if (el && el.contains(e.target as Node)) {
          const { scrollTop, scrollHeight, clientHeight } = el;
          const canScroll = scrollHeight > clientHeight;
          if (canScroll) return; // let touch scroll work inside
        }
      }
      e.preventDefault();
    };

    win.addEventListener('wheel', onWheel, { passive: false });
    win.addEventListener('touchmove', onTouchMove, { passive: false });

    return () => {
      win.removeEventListener('wheel', onWheel);
      win.removeEventListener('touchmove', onTouchMove);
    };
  }, [isOpen]);


  const scrollToBottom = (delay = 0) => {
    setTimeout(() => {
      requestAnimationFrame(() => {
        const container = messagesContainerRef.current;
        if (container) {
          container.scrollTop = container.scrollHeight;
        }
      });
    }, delay);
  };

  useEffect(() => {
    if (view === 'chat') {
      scrollToBottom(messagesContainerRef.current ? 0 : 50);
    }
  }, [messages, isTyping, view, showActions]);

  const handleQuickQuestion = (q: QuickQuestion) => {
    const userMsg: Message = { id: msgId++, from: 'user', text: q.label, seen: false };
    setActiveQuestion(q);
    setShowActions(false);
    setMessages(prev => [...prev, userMsg]);
    setView('chat');

    setTimeout(() => {
      setMessages(prev =>
        prev.map(m => m.id === userMsg.id ? { ...m, seen: true } : m)
      );
    }, 600);

    setTimeout(() => {
      setIsTyping(true);
    }, 800);

    const delay = typingDelay(q.answer);

    setTimeout(() => {
      setIsTyping(false);
      const botMsg: Message = { id: msgId++, from: 'bot', text: q.answer, typewriter: true };
      setMessages(prev => [...prev, botMsg]);
    }, 800 + delay);
  };

  const handleTypewriterDone = (id: number) => {
    setMessages(prev =>
      prev.map(m => m.id === id ? { ...m, typewriter: false } : m)
    );
    setShowActions(true);
  };

  const handleSectionLink = (sectionId: string) => {
    const el = document.getElementById(sectionId);
    if (el) {
      setIsOpen(false);
      el.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleAskAnother = () => {
    setShowActions(false);
    setActiveQuestion(null);
    setView('questions');
  };

  const handleContactPrompt = () => {
    const el = document.getElementById('contact');
    if (el) {
      setIsOpen(false);
      el.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <>
      {/* Floating Button */}
      <motion.button
        className={`chatbot-fab ${hasUnread ? 'chatbot-fab--unread' : ''} ${isOpen ? 'chatbot-fab--open' : ''}`}
        onClick={() => setIsOpen(prev => !prev)}
        whileHover={{ scale: 1.08 }}
        whileTap={{ scale: 0.95 }}
        aria-label="Open chat"
      >
        <AnimatePresence mode="wait">
          {isOpen ? (
            <motion.span key="close" initial={{ rotate: -90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: 90, opacity: 0 }} transition={{ duration: 0.2 }}>
              <FiX />
            </motion.span>
          ) : (
            <motion.span key="open" initial={{ rotate: 90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: -90, opacity: 0 }} transition={{ duration: 0.2 }}>
              <RiSparklingFill />
            </motion.span>
          )}
        </AnimatePresence>
        {hasUnread && !isOpen && <span className="chatbot-fab__badge" />}
      </motion.button>

      {/* Chat Window */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            ref={chatWindowRef}
            className="chatbot-window"
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.25, ease: 'easeOut' }}
          >
            {/* Header */}
            <div className="chatbot-window__header">
              {view === 'chat' && (
                <button className="chatbot-window__back" onClick={() => setView('questions')}>
                  <FiArrowLeft />
                </button>
              )}
              {view === 'questions' && (
                <div className="chatbot-window__avatar"><RiSparklingFill /></div>
              )}
              <div className="chatbot-window__meta">
                <span className="chatbot-window__name">Ameer's Assistant</span>
                <span className="chatbot-window__status">
                  <span className="chatbot-window__status-dot" />
                  {isTyping ? 'Typing...' : 'Online'}
                </span>
              </div>
              <button className="chatbot-window__close" onClick={() => setIsOpen(false)}>
                <FiX />
              </button>
            </div>

            <AnimatePresence mode="wait">
              {view === 'questions' ? (
                <motion.div
                  key="questions"
                  className="chatbot-view"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.2 }}
                >
                  {/* Greeting */}
                  <div className="chatbot-greeting">
                    <div className="chatbot-greeting__avatar"><RiSparklingFill /></div>
                    <div className="chatbot-greeting__bubble">
                      Hi! 👋 I'm Ameer's AI assistant. Pick a question below or get in touch directly.
                    </div>
                  </div>

                  {/* Quick Questions */}
                  <div className="chatbot-window__quick">
                    <p className="chatbot-window__quick-label">Quick questions</p>
                    <div className="chatbot-window__quick-list">
                      {QUICK_QUESTIONS.map((q) => (
                        <button
                          key={q.label}
                          className="chatbot-quick-btn"
                          onClick={() => handleQuickQuestion(q)}
                        >
                          {q.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Footer CTA */}
                  <div className="chatbot-window__footer">
                    <button className="chatbot-window__contact-btn" onClick={handleContactPrompt}>
                      <FiSend />
                      Get in touch with Ameer directly
                    </button>
                  </div>
                </motion.div>
              ) : (
                <motion.div
                  key="chat"
                  className="chatbot-view"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  transition={{ duration: 0.2 }}
                  onAnimationComplete={() => scrollToBottom()}
                >
                  {/* Messages */}
                  <div className="chatbot-window__messages" ref={messagesContainerRef}>
                    {messages.map((msg) => (
                      <motion.div
                        key={msg.id}
                        className={`chatbot-msg chatbot-msg--${msg.from}`}
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.25 }}
                      >
                        {msg.from === 'bot' && (
                          <div className="chatbot-msg__avatar"><RiSparklingFill /></div>
                        )}
                        {msg.from === 'bot' ? (
                          <BotMessage msg={msg} onDone={handleTypewriterDone} onTick={scrollToBottom} />
                        ) : (
                          <div className="chatbot-msg__user-wrap">
                            <div className="chatbot-msg__bubble">{msg.text}</div>
                            <AnimatePresence>
                              {msg.seen && (
                                <motion.span
                                  className="chatbot-msg__seen"
                                  initial={{ opacity: 0, y: 4 }}
                                  animate={{ opacity: 1, y: 0 }}
                                  transition={{ duration: 0.3 }}
                                >
                                  <FiCheck /> Seen
                                </motion.span>
                              )}
                            </AnimatePresence>
                          </div>
                        )}
                      </motion.div>
                    ))}

                    {isTyping && (
                      <motion.div
                        className="chatbot-msg chatbot-msg--bot"
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.2 }}
                      >
                        <div className="chatbot-msg__avatar"><RiSparklingFill /></div>
                        <div className="chatbot-msg__bubble chatbot-msg__bubble--typing">
                          <span className="typing-dot" />
                          <span className="typing-dot" />
                          <span className="typing-dot" />
                        </div>
                      </motion.div>
                    )}

                    {/* Action bar — appears after typewriter finishes */}
                    <AnimatePresence>
                      {showActions && activeQuestion && (
                        <motion.div
                          className="chatbot-actions"
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0 }}
                          transition={{ duration: 0.3 }}
                        >
                          {activeQuestion.sectionLink && (
                            <button
                              className="chatbot-actions__section-btn"
                              onClick={() => handleSectionLink(activeQuestion.sectionLink!.sectionId)}
                            >
                              {activeQuestion.sectionLink.label}
                              <FiArrowRight />
                            </button>
                          )}
                          <button
                            className="chatbot-actions__another-btn"
                            onClick={handleAskAnother}
                          >
                            <FiMessageSquare />
                            Ask another question
                          </button>
                        </motion.div>
                      )}
                    </AnimatePresence>

                    <div />
                  </div>

                  {/* Footer CTA */}
                  <div className="chatbot-window__footer">
                    <button className="chatbot-window__contact-btn" onClick={handleContactPrompt}>
                      <FiSend />
                      Get in touch with Ameer directly
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

export default ChatBot;
